import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import { APOffer } from "@/apps/tatar/activityApp/views/details/offer-feature/APOffer.Interface";
import { RentalUnit } from "@/apps/tatar/cashBudget/views/tenants/TenantsInterfaces";
import { OAObject } from "@/apps/tatar/objectsApp/types/object.interface";
import FormWizard from "@/components/Form/FormWizard/FormWizard";
import LoadOverlay from "@/components/LoadOverlay";
import SendLetterAnimation from "@/components/LottieAnimations/SendLetter/SendLetterAnimation";
import ModalManager from "@/components/ModalComponent/ModalManager";
import Log from "@/debug/Log";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import i18n from "@/i18n";
import { EntityModel } from "@/redux/actions/struct/implemented/OrgaStruct";
import { useMemo } from "react";
import {
  VacancyApplication,
  VacancyContractDepositFormType,
  VacancyContractFormType,
  VacancyContractHouseRulesFormType,
  VacancyContractSummaryFormType,
} from "../../../model/VacancyApplicant.interface";
import VacancyContractUtils from "../../../vacancy-applicant/utils/VacancyContractUtils";
import { getVacancyContractSignatureHTML } from "./contract/paragraphs/VacancyContractSignature";
import VacancyContractForm from "./contract/VacancyContractForm";
import VacancyContractDepositForm from "./deposit/VacancyContractDepositForm";
import { getVacancyContractHouseRulesHtml } from "./house-rules/components/VacancyContractHouseRules";

import { useActivityConstants } from "@/apps/tatar/activityApp/ActivityHooks";
import { useActivityStruct } from "@/apps/tatar/activityApp/ActivityStructContext";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import ReactDOMServer from "react-dom/server";
import { VacancyMailSignature } from "../../../action-flow/hooks/useVacancyApplicantActivityFlow";
import { VacancyContractDigitalSignerConfig } from "../../../model/VacancyContract.interface";
import VacancyContractDataLoader from "./contract/VacancyContractDataLoader";
import VacancyContractSummaryForm from "./summary/VacancyContractSummaryForm";

type Props = {
  activity: APActivity;
  offer: APOffer;
  application: VacancyApplication;
  closeModal: () => void;
  reload: () => void;
};

const VacancyContractWizzard = (props: Props) => {
  const { activity, application, offer, closeModal, reload } = props;

  return (
    <div className="vacancy-applicant-contract-wizzard">
      <VacancyContractDataLoader activity={activity}>
        {(rentalUnit: RentalUnit, object: OAObject, entity: EntityModel) => {
          return (
            <WizzardContent
              closeModal={closeModal}
              reload={reload}
              activity={activity}
              application={application}
              object={object}
              rentalUnit={rentalUnit}
              entity={entity}
              offer={offer}
            />
          );
        }}
      </VacancyContractDataLoader>
    </div>
  );
};

export default VacancyContractWizzard;

type WizzardContentProps = {
  activity: APActivity;
  object: OAObject;
  entity: EntityModel;
  offer: APOffer;
  application: VacancyApplication;
  rentalUnit: RentalUnit;
  closeModal: () => void;
  reload: () => void;
};

const WizzardContent = (props: WizzardContentProps) => {
  const {
    activity,
    object,
    entity,
    application,
    rentalUnit,
    offer,
    closeModal,
    reload,
  } = props;
  const constants = useActivityConstants();

  const activityStruct = useActivityStruct();
  const structData = activityStruct?.getAllData() || [];
  const vacancyConfig = structData.find((s) => {
    return s.data.activity_type === `activity-${constants.serviceUrl}`;
  }) as any;

  const digitalRentalAgreementSigners =
    vacancyConfig?.data?.digitalRentalAgreementSigners || [];
  const defaultDigitalSigner: VacancyContractDigitalSignerConfig =
    digitalRentalAgreementSigners.find(
      (signer: VacancyContractDigitalSignerConfig) => signer?.isDefault
    ) || digitalRentalAgreementSigners[0];

  const brandId = constants.fields.vacancyApplicationApproval?.brandId;
  const onBehalfOf =
    constants.fields.vacancyApplicationApproval?.digitalContractOnBehalfOf;

  const submitAndSendContract = async (
    results: any,
    digitalSignerId: string
  ) => {
    try {
      const contractForm = results[0] as VacancyContractFormType;
      const houseRulesForm: VacancyContractHouseRulesFormType = {
        templatePrefix: "",
        templateContent: getVacancyContractHouseRulesHtml(),
        templateSuffix: getVacancyContractSignatureHTML({
          applicantCount: contractForm.tenants.length,
        }),
      };

      const formStates = {
        contractForm: contractForm,
        depositForm: results[1] as VacancyContractDepositFormType,
        summaryForm: results[2] as VacancyContractSummaryFormType,
        houseRulesForm: houseRulesForm,
      };

      const conditions = {
        heatingCostsInclusive: formStates.contractForm.heatingCostsInclusive,
      };

      const response = await VacancyContractUtils.submitAndSendContract({
        formStates,
        constants,
        activity,
        offer,
        replacements,
        conditions,
        infoMailTemplate: getSigningInfoTemplate(),
        brandId,
        onBehalfOf,
        digitalSignerId,
      });

      return response;
    } catch (error) {
      Log.error(error);
    }
  };

  const { execute: executeSubmit, loading: isSendingContract } = useAsyncAction(
    { asyncFn: submitAndSendContract, minDelay: 2000 }
  );

  const replacements = useMemo(() => {
    const entityObject = entity.objects.find(
      (o) => o._id === activity.data.objectId
    );

    const bank = entity.banks.find(
      (b) => b._id === entityObject.bankInfo.mainBankAccount
    );

    const objectAddress = object.data?.feature?.address?.address.find(
      (a) => a.isMain
    );

    const replacements = {
      entityDisplayName: entity.displayName,
      entityStreet: entity?.address.street || "_",
      entityZip: entity?.address.zip || "-",
      entityCity: entity?.address.city || "-",
      senderName: entity.displayName,
      iban: bank.iban,
      bic: bank.blz,
      bankName: bank.bankName,
      objectZip: objectAddress?.postalcode || "-",
      objectCity: objectAddress?.city || "-",
      rentalUnitDisplayName: rentalUnit.data.displayName,
      rentalUnitArea: rentalUnit.data.area.toString(),
      rentalUnitFloor: (rentalUnit.data.floor || 0).toString(),
      rentalUnitFloorDisplayName:
        VacancyContractUtils.calculateFloorDisplayName(rentalUnit.data.floor),
    };
    return replacements;

    // disabled because all values proposed here are covered by those ids
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity._id, object._id, activity._id, application._id, rentalUnit._id]);

  return (
    <div>
      <LoadOverlay
        size="lg"
        open={isSendingContract}
        message={i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Label.SaveAndSendLoadingMessage",
          "Der Vertrag wird gespeichert und versendet."
        )}
        loadingAnimation={<SendLetterAnimation />}
        messageStyle={{ marginTop: "-100px" }}
      />
      {defaultDigitalSigner ? (
        <FormWizard
          entries={[
            {
              render: (getState, finish) => {
                return (
                  <VacancyContractForm
                    activity={activity}
                    application={application}
                    object={object}
                    rentalUnit={rentalUnit}
                    onSubmit={finish}
                    replacements={replacements}
                    currentState={getState().current}
                  />
                );
              },
            },
            {
              render: (getState, finish, goBack) => {
                const contractForm = getState()
                  .previous[0] as VacancyContractFormType;
                const tenants =
                  VacancyContractUtils.getTenantReplacements(contractForm);

                const depositReplacements = {
                  ...replacements,
                  ...tenants,
                };

                return (
                  <VacancyContractDepositForm
                    onSubmit={finish}
                    goBack={goBack}
                    previousState={getState().previous[0]}
                    currentState={getState().current}
                    replacements={depositReplacements}
                    activity={activity}
                    object={object}
                    rentalUnit={rentalUnit}
                  />
                );
              },
            },
            // {
            //   render: (getState, finish, goBack) => {
            //     const contractFormState = getState()
            //       .previous[0] as VacancyApplicantContractFormType;
            //     return (
            //       <VacancyApplicantContractHouseRulesForm
            //         onSubmit={finish}
            //         goBack={goBack}
            //         replacements={replacements}
            //         previousState={contractFormState}
            //       />
            //     );
            //   },
            // },
            {
              render: (getState, finish, goBack) => {
                const previousStates = getState().previous;
                const contractFormState =
                  previousStates[0] as VacancyContractFormType;
                const depositFormState =
                  previousStates[1] as VacancyContractDepositFormType;
                // const houseRulesFormState =
                //   previousStates[2] as VacancyApplicantContractHouseRulesFormType;

                const totalReplacements =
                  VacancyContractUtils.getReplacementsWithFormData(
                    replacements,
                    contractFormState,
                    depositFormState
                  );

                const conditions = {
                  heatingCostsInclusive:
                    contractFormState.heatingCostsInclusive,
                };

                return (
                  <VacancyContractSummaryForm
                    tenantCount={contractFormState.tenants.length}
                    onSubmit={finish}
                    goBack={goBack}
                    replacements={totalReplacements}
                    conditions={conditions}
                    contractStateTemplate={{
                      templateContent: contractFormState.templateContent,
                      templatePrefix: contractFormState.templatePrefix,
                      templateSuffix: contractFormState.templateSuffix,
                    }}
                    depositTemplate={{
                      templateContent: depositFormState.templateContent,
                      templatePrefix: depositFormState.templatePrefix,
                      templateSuffix: depositFormState.templateSuffix,
                    }}
                    houseRulesTemplate={{
                      templatePrefix: "",
                      templateContent: getVacancyContractHouseRulesHtml(),
                      templateSuffix: getVacancyContractSignatureHTML({
                        applicantCount: contractFormState.tenants.length,
                      }),
                    }}
                  />
                );
              },
            },
          ]}
          onSubmit={async (
            results: [
              VacancyContractFormType,
              VacancyContractDepositFormType,
              VacancyContractSummaryFormType
            ]
          ) => {
            const summaryResults = results[2] as VacancyContractSummaryFormType;
            const isSignedDigital = summaryResults.pdfCreation === "boldsign";

            ModalManager.confirm({
              title: isSignedDigital
                ? i18n.t(
                    "apTemplate:Vacancy.Applicant.Contract.Label.SendTitle",
                    "Vertrag erstellen und absenden"
                  )
                : i18n.t(
                    "apTemplate:Vacancy.Applicant.Contract.Label.SendTitlePDF",
                    "Vertrag erstellen"
                  ),
              message: isSignedDigital ? (
                <BoldSignComfirmMessage results={results} />
              ) : (
                <ManualSignConfirmMessage />
              ),
              confirmButtonText: isSignedDigital
                ? i18n.t(
                    "apTemplate:Vacancy.Applicant.Contract.Label.SendConfirmBtn",
                    "Vertrag speichern und senden"
                  )
                : i18n.t(
                    "apTemplate:Vacancy.Applicant.Contract.Label.SendConfirmBtnPDF",
                    "Vertrag als PDF speichern"
                  ),
              onConfirm: async () => {
                await executeSubmit(results, defaultDigitalSigner.userId);
                reload();
                closeModal();
              },
            });
          }}
        />
      ) : (
        <div className="padding-20 padding-bottom-0">
          <div className="__h1">
            {i18n.t(
              "apTemplate:Vacancy.Applicant.Contract.Label.NoDigitalSigner.Title",
              "Fehler bei der Vertragserstellung"
            )}
          </div>
          <div className="__sub-text padding-top-6">
            {i18n.t(
              "apTemplate:Vacancy.Applicant.Contract.Label.NoDigitalSigner.Message",
              "Es wurde kein digitaler Unterzeichner gefunden. Bitte wenden Sie sich an den Administrator."
            )}
          </div>
          <BFButton
            appearance="link"
            onClick={closeModal}
            className="margin-left-auto"
          >
            {i18n.t("Globals.Buttons.Close", "Schließen")}
          </BFButton>
        </div>
      )}
    </div>
  );
};

type BoldSignComfirmMessageProps = {
  results: [
    VacancyContractFormType,
    VacancyContractDepositFormType,
    VacancyContractSummaryFormType
  ];
};

const BoldSignComfirmMessage = (props: BoldSignComfirmMessageProps) => {
  const { results } = props;

  const contractFormResults = results[0] as VacancyContractFormType;

  return (
    <div>
      <div>
        {i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Label.SendMessage",
          "Sind Sie sicher, dass Sie den Vertrag speichern und versenden möchten? Nach dem Versand werden alle eingegebenen Daten endgültig übernommen und können nicht mehr geändert werden."
        )}
      </div>
      <div className="padding-top-20">
        {i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Label.SendMessageMails",
          "Der Vertrag wird an die folgenden E-Mail-Adressen gesendet:"
        )}
      </div>
      <div className="padding-top-10">
        {contractFormResults.tenants.map((tenant: any, index: number) => {
          return (
            <div
              key={`${tenant.firstName}${tenant.lastName}${tenant.email}`}
              className="bold"
            >
              {tenant.firstName} {tenant.lastName} ({tenant.email})
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ManualSignConfirmMessage = () => {
  return (
    <div>
      <div>
        {i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Label.SendMessage",
          "Sind Sie sicher, dass Sie den Vertrag speichern und versenden möchten? Nach dem Versand werden alle eingegebenen Daten endgültig übernommen und können nicht mehr geändert werden."
        )}
      </div>
      <div className="padding-top-20">
        {i18n.t(
          "apTemplate:Vacancy.Applicant.Contract.Label.SendMessagePDF",
          "Der Vertrag wird als PDF erstellt und muss manuell versendet werden."
        )}
      </div>
    </div>
  );
};

const getSigningInfoTemplate = () => {
  return ReactDOMServer.renderToString(
    <>
      <SigningInfoTemplate />
      <VacancyMailSignature />
    </>
  );
};

const SigningInfoTemplate = () => {
  return (
    <div>
      <p>
        <span
          className="mention-variable"
          data-variable-id="tenantSalutationText"
        >
          $Tenant-Salutation
        </span>
      </p>
      <p>
        wir freuen uns, Ihnen mitteilen zu können, dass wir uns für Sie als
        Mieter/in entschieden haben. In Kürze erhalten Sie eine separate E-Mail
        mit Ihrem Mietvertrag. Da wir auf Nachhaltigkeit und effiziente Prozesse
        setzen, erfolgt die Vertragsunterzeichnung vollständig digital.
      </p>
      <p>
        Bitte beachten Sie: • Sie werden gebeten, drei Unterschriften zu
        leisten:
        <ol>
          <li>Mietvertrag</li>
          <li>Kautionsvereinbarung</li>
          <li>Hausordnung</li>
        </ol>
      </p>
      <p>
        Nach der digitalen Unterschrift senden Sie den Vertrag einfach per
        Mausklick an uns zurück. Sobald uns Ihre unterzeichneten Unterlagen
        vorliegen, erhalten Sie von uns den gegengezeichneten Vertrag zur
        eigenen Ablage und zum Download. Die weiteren Schritte, wie die
        Wohnungsübergabe, besprechen wir im Anschluss persönlich mit Ihnen. Bei
        Fragen stehen wir Ihnen jederzeit gern zur Verfügung.
      </p>
    </div>
  );
};
